/**
 * Cart Event Manager
 * Système centralisé de gestion des événements du panier
 * Remplace les setTimeout() par un système d'événements prévisible
 *
 * Monitoring : window.CartPerf (toujours actif, overhead minimal)
 *   CartPerf.last()        → dernier cycle
 *   CartPerf.summary(n)    → console.table des n derniers cycles
 *   CartPerf.average(n)    → moyennes sur n cycles
 *   CartPerf.export()      → JSON complet
 *   CartPerf.reset()       → vider l'historique
 */

(function() {
  'use strict';

  var DEBUG = false;
  var MAX_HISTORY = 50;
  var cycleCounter = 0;

  // ============================================
  // SYSTÈME DE PERFORMANCE (performance.mark/measure + CartPerf)
  // ============================================

  // Cycle en cours de collecte
  var currentCycle = null;

  function newCycle() {
    cycleCounter++;
    currentCycle = {
      id: cycleCounter,
      action: CartEventManager.currentContext.action || 'unknown',
      variantId: CartEventManager.currentContext.variantId || null,
      page: window.location.pathname,
      timestamp: Date.now(),
      phases: {},
      logs: []
    };
  }

  function finalizeCycle() {
    if (!currentCycle) return;
    // Calculer le temps total perçu par l'utilisateur (depuis setContext)
    if (CartEventManager.currentContext.perfStart) {
      currentCycle.phases['0. Total User-Perceived'] = {
        duration: Math.round((performance.now() - CartEventManager.currentContext.perfStart) * 100) / 100
      };
    }
    CartPerfStore.history.push(currentCycle);
    if (CartPerfStore.history.length > MAX_HISTORY) {
      CartPerfStore.history.shift();
    }
    currentCycle = null;
    persistToStorage();
  }

  // Préfixe unique pour éviter les collisions avec d'autres marks
  var MARK_PREFIX = 'cart:';

  var PerformanceLogger = {
    timings: {},
    startTime: null,

    start: function(label) {
      var markName = MARK_PREFIX + label + ':start';
      this.timings[label] = {
        start: performance.now(),
        end: null,
        duration: null
      };
      try { performance.mark(markName); } catch(e) {}
      if (DEBUG) {
        console.log('[PERF] START: ' + label);
      }
    },

    end: function(label) {
      if (!this.timings[label]) return;
      this.timings[label].end = performance.now();
      this.timings[label].duration = this.timings[label].end - this.timings[label].start;
      var duration = this.timings[label].duration;

      var markStart = MARK_PREFIX + label + ':start';
      var markEnd = MARK_PREFIX + label + ':end';
      var measureName = MARK_PREFIX + label;
      try {
        performance.mark(markEnd);
        performance.measure(measureName, markStart, markEnd);
      } catch(e) {}

      // Stocker dans le cycle courant
      if (currentCycle) {
        currentCycle.phases[label] = { duration: Math.round(duration * 100) / 100 };
      }

      if (DEBUG) {
        console.log('[PERF] END: ' + label + ' - ' + duration.toFixed(2) + 'ms');
      }
    },

    log: function(message, data) {
      if (currentCycle) {
        currentCycle.logs.push({ label: message, data: data || null });
      }
      if (DEBUG) {
        console.log('[PERF] ' + message, data || '');
      }
    },

    summary: function() {
      if (DEBUG) {
        console.group('[PERF] RESUME DES PERFORMANCES');
        var total = 0;
        var keys = Object.keys(this.timings);
        for (var i = 0; i < keys.length; i++) {
          var timing = this.timings[keys[i]];
          if (timing.duration !== null) {
            total += timing.duration;
            console.log(keys[i] + ': ' + timing.duration.toFixed(2) + 'ms');
          }
        }
        console.log('TOTAL: ' + total.toFixed(2) + 'ms');
        console.groupEnd();
      }
    },

    reset: function() {
      // Nettoyer les marks du cycle précédent
      try {
        var entries = performance.getEntriesByType('mark');
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].name.indexOf(MARK_PREFIX) === 0) {
            performance.clearMarks(entries[i].name);
          }
        }
        var measures = performance.getEntriesByType('measure');
        for (var j = 0; j < measures.length; j++) {
          if (measures[j].name.indexOf(MARK_PREFIX) === 0) {
            performance.clearMeasures(measures[j].name);
          }
        }
      } catch(e) {}

      this.timings = {};
      this.startTime = performance.now();
    },

    logContext: function() {
      if (DEBUG && CartEventManager.currentContext.action) {
        var ctx = CartEventManager.currentContext;
        var actionLabels = {
          'open': 'Ouverture drawer',
          'add': 'Ajout produit',
          'remove': 'Suppression produit',
          'qty_change': 'Changement quantite',
          'auto_free': 'Auto-free products'
        };
        var label = actionLabels[ctx.action] || ctx.action;
        console.log('[PERF] CONTEXTE: ' + label, ctx.variantId ? '(variant: ' + ctx.variantId + ')' : '');
      }
    }
  };

  // ============================================
  // CARTPERF — API PUBLIQUE DE MONITORING
  // ============================================

  var STORAGE_KEY = 'CartPerf_history';
  var STORAGE_COUNTER_KEY = 'CartPerf_counter';

  var CartPerfStore = { history: [] };
  try {
    var stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) { CartPerfStore.history = JSON.parse(stored); }
    var storedCounter = sessionStorage.getItem(STORAGE_COUNTER_KEY);
    if (storedCounter) { cycleCounter = parseInt(storedCounter, 10) || 0; }
  } catch(e) {}

  function persistToStorage() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(CartPerfStore.history));
      sessionStorage.setItem(STORAGE_COUNTER_KEY, String(cycleCounter));
    } catch(e) {}
  }

  window.CartPerf = {
    /** Accès direct à l'historique */
    get history() { return CartPerfStore.history; },

    /** Dernier cycle complet */
    last: function() {
      var h = CartPerfStore.history;
      if (h.length === 0) { console.log('[CartPerf] Aucun cycle enregistre.'); return null; }
      var cycle = h[h.length - 1];
      console.table(cycle.phases);
      return cycle;
    },

    /** Console.table des n derniers cycles (default 10) */
    summary: function(n) {
      n = n || 10;
      var h = CartPerfStore.history;
      var slice = h.slice(-n);
      if (slice.length === 0) { console.log('[CartPerf] Aucun cycle enregistre.'); return; }

      var rows = [];
      for (var i = 0; i < slice.length; i++) {
        var c = slice[i];
        var row = {
          '#': c.id,
          action: c.action,
          page: c.page || '-',
          'user-perceived': (c.phases['0. Total User-Perceived'] || {}).duration || '-',
          total: (c.phases['renderCart() - Total'] || {}).duration || '-'
        };
        // Ajouter chaque phase connue
        var phaseLabels = [
          '1. HTML Inserted',
          '2. Group Packages',
          '3a. Update Gift Bar',
          '3b. Update Cart Data (AJAX)',
          '4. Delivery Dates',
          '5. Auto Free Products'
        ];
        for (var j = 0; j < phaseLabels.length; j++) {
          var p = phaseLabels[j];
          row[p] = (c.phases[p] || {}).duration || '-';
        }
        rows.push(row);
      }
      console.table(rows);
      return rows;
    },

    /** Moyennes sur les n derniers cycles (default 10) */
    average: function(n) {
      n = n || 10;
      var h = CartPerfStore.history;
      var slice = h.slice(-n);
      if (slice.length === 0) { console.log('[CartPerf] Aucun cycle enregistre.'); return null; }

      var sums = {};
      var counts = {};
      for (var i = 0; i < slice.length; i++) {
        var phases = slice[i].phases;
        var keys = Object.keys(phases);
        for (var j = 0; j < keys.length; j++) {
          var k = keys[j];
          if (phases[k].duration !== undefined && phases[k].duration !== null) {
            sums[k] = (sums[k] || 0) + phases[k].duration;
            counts[k] = (counts[k] || 0) + 1;
          }
        }
      }

      var result = { cycles: slice.length };
      var sumKeys = Object.keys(sums);
      for (var m = 0; m < sumKeys.length; m++) {
        var sk = sumKeys[m];
        result[sk] = Math.round((sums[sk] / counts[sk]) * 100) / 100;
      }
      console.table(result);
      return result;
    },

    /** Réinitialiser l'historique */
    reset: function() {
      CartPerfStore.history = [];
      cycleCounter = 0;
      try {
        sessionStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_COUNTER_KEY);
      } catch(e) {}
      console.log('[CartPerf] Historique reinitialise.');
    },

    /** Exporter en JSON (copier/coller) */
    export: function() {
      var json = JSON.stringify(CartPerfStore.history, null, 2);
      console.log('[CartPerf] ' + CartPerfStore.history.length + ' cycles exportes. Copier le JSON ci-dessous :');
      console.log(json);
      return CartPerfStore.history;
    },

    /** Activer/désactiver les logs console détaillés */
    debug: function(enabled) {
      DEBUG = !!enabled;
      console.log('[CartPerf] Debug ' + (DEBUG ? 'active' : 'desactive'));
    }
  };

  // ============================================
  // ÉTATS ET CONFIGURATION
  // ============================================

  var CartEventManager = {
    isRendering: false,
    isGrouping: false,
    isProcessing: false,

    currentContext: {
      action: null,
      productId: null,
      variantId: null,
      timestamp: null
    },

    phases: {
      DOM_INSERTED: 'cart:dom:inserted',
      PACKAGES_GROUPED: 'cart:packages:grouped',
      DATA_READY: 'cart:data:ready',
      DELIVERY_DATES_CALCULATED: 'cart:delivery:calculated',
      COMPLETE: 'cart:update:complete'
    },

    timestamps: {}
  };

  function dispatchPhase(phaseName, detail) {
    detail = detail || {};
    CartEventManager.timestamps[phaseName] = Date.now();

    var event = new CustomEvent(phaseName, {
      detail: {
        timestamp: Date.now(),
        ...detail
      },
      bubbles: true,
      cancelable: false
    });

    PerformanceLogger.log('Phase: ' + phaseName, detail);
    document.dispatchEvent(event);
  }

  // ============================================
  // INTÉGRATION AVEC RENDERCART
  // ============================================

  function wrapRenderCart(originalRenderCart) {
    return function() {
      // Finaliser le cycle précédent s'il est encore ouvert
      if (PerformanceLogger.timings['renderCart() - Total'] &&
          PerformanceLogger.timings['renderCart() - Total'].end === null) {
        PerformanceLogger.end('renderCart() - Total');
        finalizeCycle();
        var timing = PerformanceLogger.timings['renderCart() - Total'];
        if (timing && timing.duration && timing.duration > 10000) {
          console.warn('[CartEventManager] Timer renderCart() - Total anormalement long:', timing.duration.toFixed(2) + 'ms');
        }
      }

      PerformanceLogger.reset();
      newCycle();
      PerformanceLogger.logContext();
      PerformanceLogger.start('renderCart() - Total');
      CartEventManager.isRendering = true;

      var result = originalRenderCart.apply(this, arguments);
      return result;
    };
  }

  function onCartHTMLInserted() {
    // Si aucun cycle en cours (wrapper non exécuté), en créer un
    if (!currentCycle) {
      PerformanceLogger.reset();
      newCycle();
      PerformanceLogger.start('renderCart() - Total');
    }

    PerformanceLogger.start('1. HTML Inserted');

    dispatchPhase(CartEventManager.phases.DOM_INSERTED);

    if (typeof window.groupCartByPackages === 'function') {
      CartEventManager.isGrouping = true;
      PerformanceLogger.start('2. Group Packages');

      try {
        window.groupCartByPackages();
        PerformanceLogger.end('2. Group Packages');
        dispatchPhase(CartEventManager.phases.PACKAGES_GROUPED);
      } catch (error) {
        console.error('[CartEventManager] Erreur lors du regroupement:', error);
        PerformanceLogger.end('2. Group Packages');
        dispatchPhase(CartEventManager.phases.PACKAGES_GROUPED, { error: true });
      } finally {
        CartEventManager.isGrouping = false;
      }
    } else {
      dispatchPhase(CartEventManager.phases.PACKAGES_GROUPED, { skipped: true });
    }

    PerformanceLogger.end('1. HTML Inserted');
  }

  function onCartDataReady() {
    PerformanceLogger.end('3. Update Cart Data');
    dispatchPhase(CartEventManager.phases.DATA_READY);
  }

  function onDeliveryDatesCalculated() {
    PerformanceLogger.end('4. Delivery Dates');
    dispatchPhase(CartEventManager.phases.DELIVERY_DATES_CALCULATED);
  }

  function onCartUpdateComplete() {
    CartEventManager.isRendering = false;
    PerformanceLogger.end('renderCart() - Total');
    PerformanceLogger.summary();
    finalizeCycle();
    dispatchPhase(CartEventManager.phases.COMPLETE);
  }

  // ============================================
  // ÉCOUTEURS D'ÉVÉNEMENTS
  // ============================================

  function setupPhaseListeners() {
    document.addEventListener(CartEventManager.phases.PACKAGES_GROUPED, function() {
      PerformanceLogger.start('3. Update Cart Data');

      if (typeof window.updateGiftBar === 'function') {
        PerformanceLogger.start('3a. Update Gift Bar');
        window.updateGiftBar();
        PerformanceLogger.end('3a. Update Gift Bar');
      }

      if (typeof window.updateCartData === 'function') {
        window.updateCartData();
      } else {
        setTimeout(function() { onCartDataReady(); }, 50);
      }
    });

    document.addEventListener(CartEventManager.phases.DATA_READY, function() {
      PerformanceLogger.start('4. Delivery Dates');

      if (typeof window.DeliveryDateHandler !== 'undefined' &&
          typeof window.DeliveryDateHandler.updateCartDeliveryDates === 'function') {
        window.DeliveryDateHandler.updateCartDeliveryDates();
      } else if (typeof window.DeliveryDateHandler !== 'undefined' &&
                 typeof window.DeliveryDateHandler.initCartDeliveryDate === 'function') {
        window.DeliveryDateHandler.initCartDeliveryDate();
      }

      onDeliveryDatesCalculated();
    });

    document.addEventListener(CartEventManager.phases.DELIVERY_DATES_CALCULATED, async function() {
      if (typeof window.processAutoFreeProducts === 'function') {
        PerformanceLogger.start('5. Auto Free Products');
        try {
          await window.processAutoFreeProducts();
        } catch (error) {
          console.error('[CartEventManager] Erreur dans processAutoFreeProducts:', error);
        }
        PerformanceLogger.end('5. Auto Free Products');
      }

      onCartUpdateComplete();
    });

    document.addEventListener(CartEventManager.phases.COMPLETE, function() {
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { timestamp: Date.now() }
      }));
    });

    document.addEventListener('auto-free-products:cart-modified', function() {
      setTimeout(function() {
        CartEventManager.currentContext = {
          action: 'auto_free',
          productId: null,
          variantId: null,
          timestamp: Date.now()
        };
        if (typeof window.renderCart === 'function') {
          window.renderCart();
        }
      }, 0);
    });
  }

  // ============================================
  // INITIALISATION
  // ============================================

  function init() {
    setupPhaseListeners();

    if (typeof window.renderCart === 'function') {
      window.renderCart = wrapRenderCart(window.renderCart);
    } else {
      var checkRenderCart = setInterval(function() {
        if (typeof window.renderCart === 'function') {
          window.renderCart = wrapRenderCart(window.renderCart);
          clearInterval(checkRenderCart);
        }
      }, 100);

      setTimeout(function() {
        clearInterval(checkRenderCart);
      }, 5000);
    }
  }

  // ============================================
  // API PUBLIQUE
  // ============================================

  window.CartEventManager = {
    onCartHTMLInserted: onCartHTMLInserted,
    onCartDataReady: onCartDataReady,
    onDeliveryDatesCalculated: onDeliveryDatesCalculated,
    onCartUpdateComplete: onCartUpdateComplete,

    phases: CartEventManager.phases,

    isRendering: function() { return CartEventManager.isRendering; },
    isGrouping: function() { return CartEventManager.isGrouping; },

    setContext: function(action, details) {
      details = details || {};
      CartEventManager.currentContext = {
        action: action,
        productId: details.productId || null,
        variantId: details.variantId || null,
        timestamp: Date.now(),
        perfStart: performance.now()
      };
    },

    clearContext: function() {
      CartEventManager.currentContext = {
        action: null,
        productId: null,
        variantId: null,
        timestamp: null
      };
    },

    dispatchPhase: dispatchPhase,

    PerformanceLogger: PerformanceLogger
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
